/* eslint-disable jsdoc/require-jsdoc */
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { APIProvider, Map, useMap } from '@vis.gl/react-google-maps';
import {
  Alert,
  DashboardDataFilter,
  DeviceAttributesV1,
  DeviceType,
  Group,
  ParametersType,
  ParticleData,
  TimeSpan,
  TimeSpanDateAttributes
} from '../../constants/telosair-interfaces';
import { Filters } from '../../services/localstorage.service';
import useStorageState from '../../hooks/useStorageState';
import { useUnifiedData } from '../../hooks/useUnifiedData';
import MapDeviceMarker from '../../components/MapDeviceMarker';
import DeviceDetailPanel from '../../components/DeviceDetailPanel';
import ParameterLegend from '../../components/ParameterLegend';
import FloorMapCarousel from '../../components/FloorMapCarousel';
import Loader from '../../components/loader';

interface MapPageProps {
  groups: Group[];
  dataFilter: DashboardDataFilter | null;
  setDataFilter: (filter: DashboardDataFilter | null) => void;
  loading: boolean;
  timeSpan: TimeSpan;
  timeSpanDate: TimeSpanDateAttributes | null;
}

type DeviceHoverState = {
  x: number;
  y: number;
  deviceData: DeviceAttributesV1;
  particleData: ParticleData;
  alert: Alert[];
};

/**
 * Checks whether a device carries usable geographic coordinates. `x` is the
 * longitude and `y` the latitude; indoor floor-plan devices store pixel
 * positions here instead, which fall outside the valid lat/lng ranges.
 * @param device - The device to test.
 * @returns True when the device has plottable lat/lng values.
 */
function hasValidCoordinates(device: DeviceAttributesV1): boolean {
  const lat = device.y;
  const lng = device.x;
  return (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    !Number.isNaN(lat) &&
    !Number.isNaN(lng) &&
    !(lat === 0 && lng === 0) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

// Geographic center of the contiguous United States — used as the default
// viewport so the map opens on the US before fitting to any device markers.
const US_CENTER = { lat: 39.8283, lng: -98.5795 };
const US_ZOOM = 4;

type FullScreenMapProps = {
  devices: DeviceAttributesV1[];
  selectedDevice: number | null;
  displayParameter: ParametersType | null;
  setDataFilter: (filter: DashboardDataFilter | null) => void;
  timeSpan: TimeSpan;
  timeSpanDate: TimeSpanDateAttributes | null;
};

/**
 * Renders every plottable device on a single full-screen Google Map and keeps
 * all markers in view by fitting the map bounds to them. Clicking a marker
 * opens a device detail side panel.
 * @param props - Props for the full-screen map.
 * @returns A React element with the rendered map and markers.
 */
function FullScreenMap(props: FullScreenMapProps): ReactElement {
  const { devices, selectedDevice, displayParameter, setDataFilter, timeSpan, timeSpanDate } =
    props;
  const map = useMap();
  const [hovered, setHovered] = useState<DeviceHoverState | null>(null);

  useEffect(() => {
    if (!map || devices.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    devices.forEach((device) => {
      bounds.extend(new window.google.maps.LatLng(device.y, device.x));
    });
    map.fitBounds(bounds, 64);
    // Avoid an over-zoomed map when only a single device is plotted.
    if (devices.length === 1) {
      const listener = window.google.maps.event.addListenerOnce(map, 'idle', () => {
        if ((map.getZoom() ?? 0) > 17) map.setZoom(17);
      });
      // eslint-disable-next-line consistent-return
      return () => window.google.maps.event.removeListener(listener);
    }
  }, [map, devices]);

  return (
    <>
      <Map
        mapId="2710c5f22fa0158b"
        colorScheme="LIGHT"
        defaultCenter={US_CENTER}
        defaultZoom={US_ZOOM}
        gestureHandling="greedy"
        scrollwheel
        draggable
        zoomControl
        mapTypeControl={false}
        streetViewControl={false}
        fullscreenControl={false}
        onClick={() => setHovered(null)}
      >
        {devices.map((device) => (
          <MapDeviceMarker
            key={device.id}
            deviceData={device}
            selectedDevice={selectedDevice}
            displayParameter={displayParameter}
            onSelect={setHovered}
            setDataFilter={setDataFilter}
          />
        ))}
      </Map>
      {hovered && (
        <DeviceDetailPanel
          device={hovered.deviceData}
          initialParameter={displayParameter}
          timeSpan={timeSpan}
          timeSpanDate={timeSpanDate || { startDate: 0, endDate: 0 }}
          onClose={() => setHovered(null)}
        />
      )}
    </>
  );
}

/**
 * Full-screen Map view for the dashboard. Aggregates every device that carries
 * geographic coordinates across all groups and plots them on one large map.
 * @param props - Props for the map page.
 * @returns A React element representing the full-screen map view.
 */
export default function MapPage(props: MapPageProps): ReactElement {
  const { groups, dataFilter, setDataFilter, loading, timeSpan, timeSpanDate } = props;
  const { parameters } = useUnifiedData();
  const [selectedParameters, setSelectedParameters] = useStorageState<ParametersType[]>(
    Filters.dashboardFlooMapParameters,
    []
  );

  const selectedDevice = dataFilter?.type === 'device' ? dataFilter.id : null;

  // Collect every device that can be placed geographically. Only outdoor
  // groups carry real lat/lng; indoor groups store floor-plan pixel positions
  // in x/y, which must never be plotted on a world map.
  const plottableDevices = useMemo(() => {
    const seen = new Set<number>();
    const result: DeviceAttributesV1[] = [];
    groups.forEach((group) => {
      if (group.floorPlan !== DeviceType.OUTDOOR) return;
      group.sensorDetailsList?.forEach((device) => {
        if (hasValidCoordinates(device) && !seen.has(device.id)) {
          seen.add(device.id);
          result.push(device);
        }
      });
    });
    return result;
  }, [groups]);

  // Indoor groups carry a floor-plan image and pixel positions rather than
  // lat/lng, so they can still be drawn - just not on a world map.
  const indoorGroups = useMemo(
    () =>
      groups.filter(
        (group) =>
          group.floorPlan !== DeviceType.OUTDOOR && (group.sensorDetailsList?.length ?? 0) > 0
      ),
    [groups]
  );

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  // A marker badge can only carry one reading, so the legend behaves as a
  // single-select here: the first (and only) selected parameter drives every
  // marker, and an empty selection means "All" — i.e. the default AQI badge.
  const displayParameter: ParametersType | null = selectedParameters[0] ?? null;
  const displayParameterLabel =
    parameters.find((p) => p.parameterName === displayParameter)?.shortName ?? null;

  const parameterClass = (value: ParametersType): string =>
    displayParameter === value ? 'text-white' : 'text-gray-400';

  /**
   * Selects the parameter shown on every marker, or clears back to the default
   * when the active parameter is clicked again.
   * @param selectedValue - The parameter being toggled.
   */
  const handleParameterToggle = (selectedValue: string): void => {
    setSelectedParameters(
      displayParameter === selectedValue ? [] : [selectedValue as ParametersType]
    );
  };

  if (loading) {
    return (
      <div className="relative flex h-full w-full flex-1 items-center justify-center rounded-md border border-gray-300 bg-container-600 dark:border-background-700">
        <Loader />
      </div>
    );
  }

  const hasDevices = plottableDevices.length > 0;
  const showFloorPlans = !hasDevices && indoorGroups.length > 0;

  // Nothing is geo-located, but the client does have indoor groups: fall back to
  // the floor-plan carousel the Simple view uses rather than stranding the user
  // on an empty world map. Its own legend and group navigation come with it.
  if (showFloorPlans) {
    const indoorDeviceCount = indoorGroups.reduce(
      (total, group) => total + (group.sensorDetailsList?.length ?? 0),
      0
    );
    return (
      <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden rounded-md border border-gray-300 bg-container-600 dark:border-background-700">
        {/* Bottom-left: the carousel owns the top bar (legend toggle) and the
            vertically centred navigation arrows. */}
        <div className="pointer-events-none absolute bottom-3 left-3 z-[2] rounded-lg bg-container-900/80 px-3 py-1.5 text-xs font-medium text-gray-200 backdrop-blur-sm">
          <span className="text-primary-300">{indoorDeviceCount}</span> Device
          {indoorDeviceCount !== 1 ? 's' : ''} on floor plans
          <span className="mx-1.5 text-gray-500">·</span>
          <span className="text-gray-400">No map locations set</span>
        </div>
        <FloorMapCarousel
          groups={indoorGroups}
          loading={false}
          dataFilter={dataFilter}
          setDataFilter={setDataFilter}
        />
      </div>
    );
  }

  return (
    <div className="relative flex h-full w-full flex-1 overflow-hidden rounded-md border border-gray-300 bg-container-600 dark:border-background-700">
      {/* Parameter legend overlay (only relevant when markers are plotted) */}
      {hasDevices && (
        <div className="absolute left-3 top-3 z-[1] flex max-w-[70%] flex-wrap gap-2 rounded-lg bg-container-900/80 p-2 backdrop-blur-sm">
          <ParameterLegend
            handleParameterClick={() => setSelectedParameters([])}
            name="All"
            parameter={'all' as ParametersType}
            active={displayParameter === null}
            className={parameterClass('all' as ParametersType)}
          />
          {parameters.map((parameter) => (
            <ParameterLegend
              key={parameter.parameterName}
              handleParameterClick={handleParameterToggle}
              name={parameter.shortName}
              parameter={parameter.parameterName}
              active={displayParameter === parameter.parameterName}
              className={parameterClass(parameter.parameterName)}
            />
          ))}
        </div>
      )}

      {/* Device count badge + the metric the marker badges are showing */}
      <div className="absolute right-3 top-3 z-[1] rounded-lg bg-container-900/80 px-3 py-1.5 text-xs font-medium text-gray-200 backdrop-blur-sm">
        <span className="text-primary-300">{plottableDevices.length}</span> Devices on map
        {hasDevices && (
          <>
            <span className="mx-1.5 text-gray-500">·</span>
            <span className="text-primary-300">{displayParameterLabel ?? 'AQI'}</span>
          </>
        )}
      </div>

      {/* No-location hint, shown over the default US map */}
      {!hasDevices && (
        <div className="pointer-events-none absolute left-1/2 top-3 z-[1] -translate-x-1/2 rounded-lg bg-container-900/80 px-3 py-1.5 text-center text-xs text-gray-300 backdrop-blur-sm">
          No devices with map locations yet
        </div>
      )}

      <APIProvider apiKey={apiKey}>
        <FullScreenMap
          devices={plottableDevices}
          selectedDevice={selectedDevice}
          displayParameter={displayParameter}
          setDataFilter={setDataFilter}
          timeSpan={timeSpan}
          timeSpanDate={timeSpanDate}
        />
      </APIProvider>
    </div>
  );
}
